import { useContext } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BORDER, FOREGROUND, MUTED_FG, tooltipStyle, xAxisProps, yAxisProps } from "./chartTheme";
import { ChartPreviewContext } from "./ChartPreviewContext";
import MetricChartFrame from "./MetricChartFrame";

export interface MovementSeries {
  key: string;
  name: string;
  color: string;
}

interface StackedMovementsChartProps {
  caption: string;
  /** Rows keyed by `label` plus one numeric field per series and the net key. */
  data: Array<{ label: string; [key: string]: number | string }>;
  /** Bar series, stacked around zero. Values are pre-signed (losses negative). */
  series: MovementSeries[];
  netKey: string;
  netName: string;
  format: (value: number) => string;
  yWidth?: number;
  active?: string;
  breakdown?: string;
}

/**
 * The app's movements view: a stacked time series where gains stack up and losses
 * stack down from a zero line, with the net as an overlaid line. Mirrors the
 * product's POSITIVE_NEGATIVE stacked chart with a `lineMetricNames` overlay.
 */
const StackedMovementsChart = ({
  caption,
  data,
  series,
  netKey,
  netName,
  format,
  yWidth = 52,
  active = "6M",
  breakdown = "Movement type",
}: StackedMovementsChartProps) => {
  const preview = useContext(ChartPreviewContext);

  return (
    <MetricChartFrame caption={caption} chartType="Stacked" breakdown={breakdown} active={active}>
      <ComposedChart data={data} margin={{ top: 12, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
        <XAxis dataKey="label" {...xAxisProps} />
        <YAxis tickFormatter={format} width={yWidth} {...yAxisProps} />
        <Tooltip
          cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
          contentStyle={tooltipStyle}
          formatter={(value: number, name: string) => [format(value), name]}
        />
        {/* Legend crowds the compact thumbnail — only show it at full size. */}
        {!preview && <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />}
        <ReferenceLine y={0} stroke={MUTED_FG} />
        {series.map((s) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name}
            stackId="m"
            fill={s.color}
            isAnimationActive={false}
          />
        ))}
        <Line
          type="monotone"
          dataKey={netKey}
          name={netName}
          stroke={FOREGROUND}
          strokeWidth={2}
          dot={preview ? false : { r: 2.5 }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </MetricChartFrame>
  );
};

export default StackedMovementsChart;
